import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useFriendsRegistryTelemetry } from '../FriendsRegistry.telemetry';
import { TransitionalRegistriesForEventDocument, TransitionalRegistryStatus, useCreateTransitionalRegistryMutation } from '@graphql/generated';

export interface CreateTransitionalRegistryResponse {
  id?: string;
  scannedAt?: { __typename?: 'Date' | undefined; dateString: string } | null;
  status: TransitionalRegistryStatus;
}

interface CreateTransitionalRegistryProps {
  eventId: string;
  telemetryPageName: string;
  onCreateRegistry: ({ id, status }: CreateTransitionalRegistryResponse) => void;
  onError: (message?: string) => void;
}

export const useCreateTransitionalRegistry = ({ eventId, telemetryPageName, onCreateRegistry, onError }: CreateTransitionalRegistryProps) => {
  const telemetry = useFriendsRegistryTelemetry();

  const [mutation, { data, error, loading }] = useCreateTransitionalRegistryMutation({
    refetchQueries: [{ query: TransitionalRegistriesForEventDocument, variables: { eventId } }],
    onCompleted: response => {
      onCreateRegistry({
        id: response.createTransitionalRegistry.id,
        status: response.createTransitionalRegistry.status,
        scannedAt: response.createTransitionalRegistry.scannedAt
      });
    },
    onError: error => onError(error.message)
  });

  const createTransitionalRegistry = useEventCallback((url: string) => {
    telemetry.addFromFriendsRegistrySubmit(telemetryPageName, eventId, url);
    mutation({
      variables: { payload: { eventId, url } },
      onError: error => {
        onCreateRegistry({ status: TransitionalRegistryStatus.failed });
        telemetry.createTransitionalRegistryError(telemetryPageName, eventId, url, error);
      }
    });
  });

  return {
    data,
    loading,
    error,
    createTransitionalRegistry
  };
};
